import { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  CashIcon,
  CircularProgress,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import {
  StepsCarousel,
  type CarouselStep,
} from "../components/StepsCarousel";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { APP_ROUTES } from "../constants";
import { getOperation } from "../services/api";
import { isHttpUrl, returnToChat } from "../utils/returnToChat";
import { formatLabeledAmount } from "../utils";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { getEvents, safeCapture } from "../analytics";
import step1 from "../assets/cash_deposit_how_to/step1.svg?raw";
import step2 from "../assets/cash_deposit_how_to/step2.svg?raw";
import step3 from "../assets/cash_deposit_how_to/step3.svg?raw";
import step4 from "../assets/cash_deposit_how_to/step4.svg?raw";
import step5 from "../assets/cash_deposit_how_to/step5.svg?raw";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


const ILLUSTRATIONS = [step1, step2, step3, step4, step5];

/** Steps that quote the exact phrase to say at the counter. */
const SCRIPTED_STEPS = new Set(["3"]);

const STEP_KEYS = ["1", "2", "3", "4", "5"] as const;

/**
 * Post-create cash-deposit briefing. The barcode is generated later from the
 * WhatsApp link (cash-deposit flow). Find-stores opens the locator; back /
 * primary CTA return to chat.
 */
export const HowToDepositCashPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme, quoteState } = useStore();
  const { redirectTo } = useRouteWithToken();
  const { session } = useSessionStore();
  const [searchParams] = useSearchParams();
  const operationId =
    searchParams.get("operationId") ??
    session.data?.config?.usage?.operationId ??
    null;
  const chatUrl = session.data?.config?.returnToChatUrl;
  const showReturnCta = isHttpUrl(chatUrl);
  const partnerColors = session.data?.partner?.theme?.colors as
    | Record<string, string>
    | undefined;

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    footerColor,
    dropzoneColors,
    iconColors,
  } = colorConfig;
  const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);
  const inactiveColor = partnerColors?.inactived ?? footerColor;

  const linkId = session.data?.linkId;
  const [operationAmount, setOperationAmount] = useState<{
    totalCost: number;
    sourceCurrency: string;
  } | null>(null);

  useEffect(() => {
    if (quoteState?.totalCost != null || !linkId || !operationId) {
      return;
    }
    let cancelled = false;
    void getOperation(linkId, operationId)
      .then((op) => {
        if (!cancelled && op.totalCost != null) {
          setOperationAmount({
            totalCost: op.totalCost,
            sourceCurrency: op.sourceCurrency,
          });
        }
      })
      .catch(() => {
        // Keep the generic how-to amount if the operation cannot be loaded.
      });
    return () => {
      cancelled = true;
    };
  }, [quoteState?.totalCost, linkId, operationId]);

  const cashTotal =
    quoteState?.totalCost != null
      ? formatLabeledAmount(quoteState.totalCost, quoteState.sourceCurrency)
      : operationAmount
        ? formatLabeledAmount(
            operationAmount.totalCost,
            operationAmount.sourceCurrency,
          )
        : null;
  const stepAmount = cashTotal ?? t("howToDeposit.cashAmount");

  const storesMapRoute = operationId
    ? `${APP_ROUTES.STORES_MAP_PAGE}?operationId=${operationId}`
    : APP_ROUTES.STORES_MAP_PAGE;

  const steps = useMemo<CarouselStep[]>(
    () =>
      STEP_KEYS.map((key, position) => ({
        id: key,
        illustration: ILLUSTRATIONS[position],
        title: t(`howToDeposit.steps.${key}.title`),
        body: t(`howToDeposit.steps.${key}.body`, { amount: stepAmount }),
        script: SCRIPTED_STEPS.has(key)
          ? t(`howToDeposit.steps.${key}.script`)
          : undefined,
      })),
    [t, stepAmount],
  );

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("how_to_deposit_cash"));
  }, []);

  const goToStoresMap = () => {
    redirectTo(storesMapRoute);
  };

  const leaveToChat = () => {
    if (isHttpUrl(chatUrl)) {
      returnToChat(chatUrl);
    }
  };

  /**
   * The artwork ships a purple palette; these tokens repaint it from the
   * active theme. `color-mix` keeps the tint ladder proportional whatever the
   * partner's brand color is, so the drawings never fight the chrome.
   */
  const themeVars = {
    "--cdh-ink": accentSolid,
    "--cdh-mid": `color-mix(in srgb, ${accentSolid} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${accentSolid} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${accentSolid} 10%, ${background})`,
    "--cdh-neutral": inactiveColor,
    "--cdh-neutral-strong": footerColor,
    "--cdh-white": background,
    "--cdh-title": accentSolid,
    "--cdh-body": bodyCopy,
    "--cdh-muted": footerColor,
    "--cdh-accent": accentSolid,
    "--cdh-surface": background,
    "--cdh-border": inactiveColor,
  } as React.CSSProperties;

  return (
    <AuraLayout colorConfig={colorConfig} className="cdh-page">
      <Layout.Header style={themeVars}>
        <Navbar
          title={t("howToDeposit.titleHighlight")}
          color={title}
          align="left"
          onBackClick={leaveToChat}
        />
      </Layout.Header>

      <Layout.Content className="cdh-section" style={themeVars}>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="cdh-hero">
          <CircularProgress
            variant="full"
            size={56}
            strokeWidth={3}
            colors={dropzoneColors}
            className="cdh-hero-mark"
          >
            <span className="cdh-hero-mark-inner" style={{ background }}>
              <CashIcon size={22} colors={iconColors} />
            </span>
          </CircularProgress>
          <PageTitle
            highlight={t("howToDeposit.titleSecondary")}
            highlightColor={accentSolid}
            secudnary=""
            secudnaryColor={accentSolid}
          />
        </section>

        <StepsCarousel
          steps={steps}
          regionLabel={t("howToDeposit.title")}
          stepLabel={(position, total) =>
            t("howToDeposit.stepOf", { current: position + 1, total })
          }
          goToLabel={(position) =>
            t("howToDeposit.goToStep", { step: position + 1 })
          }
        />
      </Layout.Content>

      <Layout.Footer style={themeVars}>
        <section className="footer-section cdh-footer">
          <BodyCopy textColor={footerColor} className="cdh-store-count">
            {t("howToDeposit.storeCount")}
          </BodyCopy>
          <Button
            type="button"
            size="large"
            variant="outline"
            className="cdh-secondary-cta"
            /* `outline` paints its ring from `background`, so the secondary CTA
               echoes the primary's gradient instead of the kit's black default. */
            background={backgroundBtn}
            color={accentSolid}
            style={{ backgroundColor: background }}
            onClick={goToStoresMap}
          >
            {t("howToDeposit.findStores")}
          </Button>
          {showReturnCta ? (
            <Button
              type="button"
              size="large"
              className="lola-return-chat"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={leaveToChat}
            >
              {t("howToDeposit.backToChat")}
            </Button>
          ) : null}
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
